"use client";

import { create } from "zustand";
import { useEffect, useRef } from "react";
import { queryClient } from "@/config/react-query";
import { useUser } from "@/contexts/user-provider";
import { useWebSocketCallbackSubscription } from "@/hooks/use-websocket-subscription";
import type { AlertDetail } from "@/types/alert";
import { AlertType } from "@/types/enums/alert-type";
import type { TeamMessageSummary } from "@/types/team-chat";
import { incrementUnreadCount, replaceOptimisticOrPrependTeamMessage, teamChatQueryKeys, updateConversationInList, updateMessageReactionsInCache, updateReadByInCache, updateTeamMessageInCache } from "@/query/team-chat";
import { useTeamChatDrawerStore } from "@/stores/team-chat-store";
const TEAM_CHAT_ALERT_TYPES = [AlertType.TEAM_MESSAGE_RECEIVED, AlertType.TEAM_CONVERSATION_UPDATED, AlertType.TEAM_TYPING_INDICATOR, AlertType.TEAM_MESSAGE_EDITED, AlertType.TEAM_PRESENCE_UPDATE, AlertType.TEAM_MESSAGE_REACTION, AlertType.TEAM_READ_RECEIPT];
interface TypingStore {
  /** Map of conversationId -> Set of providerIds currently typing */
  typing: Record<string, Set<string>>;
  addTyping: (conversationId: string, providerId: string) => void;
  removeTyping: (conversationId: string, providerId: string) => void;
}
export const useTypingStore = create<TypingStore>(set => ({
  typing: {},
  addTyping: (conversationId, providerId) => set(state => {
    const current = new Set(state.typing[conversationId] ?? []);
    current.add(providerId);
    return {
      typing: {
        ...state.typing,
        [conversationId]: current
      }
    };
  }),
  removeTyping: (conversationId, providerId) => set(state => {
    const current = new Set(state.typing[conversationId] ?? []);
    current.delete(providerId);
    const newTyping = {
      ...state.typing
    };
    if (current.size === 0) {
      delete newTyping[conversationId];
    } else {
      newTyping[conversationId] = current;
    }
    return {
      typing: newTyping
    };
  })
}));
export function useTeamChatRealtime() {
  const typingTimeoutsRef = useRef<Record<string, Record<string, NodeJS.Timeout>>>({});
  const {
    currentUser
  } = useUser();

  // Read through refs so the message handler sees the values current when a
  // message arrives.
  const activeConversationIdRef = useRef(useTeamChatDrawerStore.getState().activeConversationId);
  const isOpenRef = useRef(useTeamChatDrawerStore.getState().isOpen);
  const currentUserIdRef = useRef(currentUser?.id);
  currentUserIdRef.current = currentUser?.id;

  // Subscribe to store changes outside of the effect
  useEffect(() => {
    const unsub = useTeamChatDrawerStore.subscribe(state => {
      activeConversationIdRef.current = state.activeConversationId;
      isOpenRef.current = state.isOpen;
    });
    return unsub;
  }, []);

  // Online providers are kept in sync via TEAM_PRESENCE_UPDATE WebSocket
  // events (connected/disconnected/status_update). No need to invalidate on
  // connect — that caused redundant refetches.

  function handleMessage(alert: AlertDetail) {
    const {
      type,
      data
    } = alert as {
      type: string;
      data: any;
    };
    if (type === AlertType.TEAM_MESSAGE_RECEIVED) {
      const message = data as TeamMessageSummary;
      const conversationId = message.conversationId;

      // Add message to cache (replaces our optimistic if WebSocket arrives before onSuccess)
      replaceOptimisticOrPrependTeamMessage(conversationId, message);

      // Update conversation list
      updateConversationInList(conversationId, conv => ({
        ...conv,
        lastMessageAt: message.createdDate,
        lastMessagePreview: message.content || (message.files?.length ? `📎 ${message.files.length} file${message.files.length > 1 ? "s" : ""}` : conv.lastMessagePreview)
      }));

      // Increment unread count if not viewing this conversation and message is from someone else
      const isViewingConversation = isOpenRef.current && activeConversationIdRef.current === conversationId;
      const isFromOther = message.sender.providerId !== currentUserIdRef.current;
      if (isFromOther && !isViewingConversation) {
        incrementUnreadCount(conversationId);
      }

      // Surfacing the message — toast, bell row, and desktop notification — is handled
      // by useInAppNotifications off the embedded ProviderNotification, the same
      // as every other alert type. This hook only maintains chat cache state.
    }
    if (type === AlertType.TEAM_CONVERSATION_UPDATED) {
      queryClient.invalidateQueries({
        queryKey: teamChatQueryKeys.conversations()
      });
    }
    if (type === AlertType.TEAM_TYPING_INDICATOR) {
      const {
        conversationId: conversationId_0,
        providerId
      } = data as {
        conversationId: string;
        providerId: string;
      };

      // Clear existing timeout for this user
      if (typingTimeoutsRef.current[conversationId_0]?.[providerId]) {
        clearTimeout(typingTimeoutsRef.current[conversationId_0][providerId]);
      }

      // Add typing state
      useTypingStore.getState().addTyping(conversationId_0, providerId);

      // Set timeout to auto-clear after 3 seconds
      if (!typingTimeoutsRef.current[conversationId_0]) {
        typingTimeoutsRef.current[conversationId_0] = {};
      }
      typingTimeoutsRef.current[conversationId_0][providerId] = setTimeout(() => {
        useTypingStore.getState().removeTyping(conversationId_0, providerId);
        delete typingTimeoutsRef.current[conversationId_0]?.[providerId];
      }, 3000);
    }
    if (type === AlertType.TEAM_MESSAGE_EDITED) {
      const message_0 = data as TeamMessageSummary;
      updateTeamMessageInCache(message_0.conversationId, message_0);
    }
    if (type === AlertType.TEAM_PRESENCE_UPDATE) {
      const {
        providerId: providerId_0,
        action,
        ...statusFields
      } = data as {
        providerId: string;
        action?: "connected" | "disconnected" | "status_update";
        status?: "ONLINE" | "WITH_CLIENT" | "BUSY";
        currentAppointment?: {
          title: string;
          endTime: string;
        } | null;
        currentBlock?: {
          title: string;
          endTime: string;
        } | null;
        customStatusText?: string | null;
        customStatusEmoji?: string | null;
        customStatusExpiresAt?: string | null;
      };
      type OnlineProvidersData = {
        providers: {
          providerId: string;
          status: "ONLINE" | "WITH_CLIENT" | "BUSY";
          currentAppointment: {
            title: string;
            endTime: string;
          } | null;
          currentBlock: {
            title: string;
            endTime: string;
          } | null;
          customStatusText: string | null;
          customStatusEmoji: string | null;
          customStatusExpiresAt: string | null;
        }[];
      };
      queryClient.setQueryData(teamChatQueryKeys.onlineProviders(), (old: OnlineProvidersData | undefined) => {
        if (!old) return old;
        const providers = [...old.providers];
        if (action === "disconnected") {
          return {
            providers: providers.filter(p => p.providerId !== providerId_0)
          };
        }
        if (action === "connected") {
          // Add if not already present, using server-provided status
          if (!providers.some(p_0 => p_0.providerId === providerId_0)) {
            providers.push({
              providerId: providerId_0,
              status: statusFields.status ?? "ONLINE",
              currentAppointment: statusFields.currentAppointment ?? null,
              currentBlock: statusFields.currentBlock ?? null,
              customStatusText: statusFields.customStatusText ?? null,
              customStatusEmoji: statusFields.customStatusEmoji ?? null,
              customStatusExpiresAt: statusFields.customStatusExpiresAt ?? null
            });
          }
          return {
            providers
          };
        }
        if (action === "status_update") {
          // Update custom status fields for existing provider
          return {
            providers: providers.map(p_1 => p_1.providerId === providerId_0 ? {
              ...p_1,
              customStatusText: statusFields.customStatusText ?? null,
              customStatusEmoji: statusFields.customStatusEmoji ?? null,
              customStatusExpiresAt: statusFields.customStatusExpiresAt ?? null
            } : p_1)
          };
        }

        // Unknown action — fall back to refetch
        return old;
      });
    }
    if (type === AlertType.TEAM_MESSAGE_REACTION) {
      const {
        messageId,
        conversationId: conversationId_1,
        reactions
      } = data as {
        messageId: string;
        conversationId: string;
        reactions: {
          emoji: string;
          count: number;
          reactedByMe: boolean;
        }[];
      };
      updateMessageReactionsInCache(conversationId_1, messageId, reactions);
    }
    if (type === AlertType.TEAM_READ_RECEIPT) {
      const receipt = data as {
        conversationId: string;
        providerId: string;
        lastReadMessageId?: string;
        lastReadMessageDate: string;
      };
      if (receipt.conversationId && receipt.providerId && receipt.lastReadMessageDate) {
        updateReadByInCache(receipt.conversationId, receipt.providerId, receipt.lastReadMessageDate);
      }
    }
  }
  useWebSocketCallbackSubscription(TEAM_CHAT_ALERT_TYPES, handleMessage, true, "useTeamChatRealtime");
}